import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertEventsService } from '../../../../development/alert-events.service';
import { EventV2Document } from '@models/alerts-v2.model';
import { smartSearch } from '../../../../helpers/common.helpers';
import { ConfirmDialogModel, UiButtonComponent } from '@ui';
import { Store } from '@ngrx/store';
import { PreloaderColor } from '@enums/shared.enum';
import { AlertsV2StoreActions } from '@states/alerts-v2/alerts-v2.action-types';
import { ofType } from '@ngrx/effects';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { CameraLookup } from '@models/camera.model';
import { features } from '@consts/text.const';
import { AlertsV2Effect } from '@effects/alerts-v2.effect';
import {
  UiTrafficControlDialogComponent,
  UiTrafficControlDialogData,
  UiTrafficControlDialogResult,
} from '../../../../shared/ui-kit/ui-traffic-control/ui-line-crossing-dialog/ui-traffic-control-dialog.component';
import * as _ from 'lodash';
import { AlertEventLineCrossing, AlertEventTrafficControl } from '@models/alert-events.model';
import * as AlertEventsActions from '@states/alert-events/alert-events.actions';
import { EdgeCamera } from '../../../../cameras/camera.model';
import { CameraPickerModels } from '@modals/camera-picker-inline/models/models';
import { CameraPickerModalComponent } from '@modals/camera-picker-modal/camera-picker-modal.component';
import { Dictionary } from '@ngrx/entity/src/models';
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';
import { ConfirmDialogService } from '../../../../shared/confirm-dialog/confirm-dialog.service';
import { AlertEventsEffect } from '@effects/alert-events.effect';
import { RouteModalService, UiNoDataComponent } from '@ui';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { TranslatePipe } from '@ngx-translate/core';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { PipesModule } from '../../../../pipes/pipes.module';
import { TruncateDirective } from '../../../../shared/directives/truncate.directive';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiCameraSnapshotComponent } from '../../../../shared/ui-kit/ui-camera-snapshot/ui-camera-snapshot.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createEntrancesTableConfig, EntranceCellContext } from './entrances-table.util';
import ZoneSelectionType = CameraPickerModels.ZoneSelectionType;
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-entrances',
  templateUrl: './entrances.component.html',
  styleUrls: ['./entrances.component.scss'],
  imports: [
    UiKitModule,
    TranslatePipe,
    LazyLoadImageModule,
    PipesModule,
    TruncateDirective,
    UiSearchComponent,
    UiNoDataComponent,
    UiButtonComponent,
    UiPreloaderComponent,
    UiCameraSnapshotComponent,
    UiDatasetTableComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntrancesComponent implements OnInit {
  public loading = signal<boolean>(true);
  public isFirstLoad = signal<boolean>(true);
  private entrances = signal<EventV2Document[]>([]);
  protected search = syncQueryParam<string>('search');

  protected readonly noResults = features.entrance.noResults;
  protected readonly noData = features.entrance.noData;

  protected readonly nameCellTemplate = viewChild<TemplateRef<EntranceCellContext>>('nameCell');

  private readonly cameraNames = this.store$.selectSignal(CameraSelectors.selectCameraNames);

  public readonly tableConfig = computed(() => {
    const nameTemplate = this.nameCellTemplate();
    if (!nameTemplate) return null;

    return createEntrancesTableConfig({
      nameCellTemplate: nameTemplate,
      getCameraName: (row) => this.getCameraNameFormatted(row),
    });
  });

  private readonly filteredEntrances = computed(() => {
    const search = this.search();
    const entrances = this.entrances();
    if (!search) {
      return entrances;
    }
    return smartSearch<EventV2Document>(Object.values(entrances ?? {}), search, 'name');
  });

  public readonly vm$ = computed(() => {
    const loading = this.loading();
    const isFirstLoad = this.isFirstLoad();
    const entrances = this.entrances();
    const filteredEntrances = this.filteredEntrances();
    const search = this.search();

    let viewState: ViewState;
    if (loading && isFirstLoad) {
      viewState = 'LOADING';
    } else if (search && !filteredEntrances.length) {
      viewState = 'NO_RESULTS';
    } else if (!entrances.length) {
      viewState = 'INITIAL_EMPTY';
    } else {
      viewState = 'HAS_DATA';
    }

    return {
      viewState,
      filteredEntrances,
      search,
    };
  });

  constructor(
    private dialog: MatDialog,
    private store$: Store,
    private alertEventsService: AlertEventsService,
    private alertsV2Effects: AlertsV2Effect,
    private alertEventsEffect: AlertEventsEffect,
    private confirm: ConfirmDialogService,
    private modalService: RouteModalService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.entrances }));

    this.getEntrances();
    this.alertsV2Effects.sendAlertEvent$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(AlertsV2StoreActions.saveAlertSuccess))
      .subscribe(() => {
        this.getEntrances();
      });

    this.alertEventsEffect.removeAlertEvent$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(AlertEventsActions.removeEventSuccess))
      .subscribe(() => {
        this.getEntrances();
      });

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createEntrance,
          open: () => this.openAddEntranceDialog(),
        },
      ],
      this.destroyRef,
    );
  }

  public getEntrances(): void {
    this.loading.set(true);
    this.alertEventsService.getEntrances().subscribe((entrances) => {
      this.entrances.set(entrances);
      this.loading.set(false);
      this.isFirstLoad.set(false);
    });
  }

  public delete(entrance: EventV2Document): void {
    this.confirm
      .open({
        title: `Delete entrance ${entrance.name}`,
        msg: `Are you sure you want to delete ${entrance.name}?`,
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        confirm: 'Delete',
        cancel: 'No',
      })
      .subscribe((res) => {
        if (!!res.selection) {
          this.store$.dispatch(
            AlertEventsActions.startRemoveEvent({
              id: entrance?._id,
            }),
          );
        }
      });
  }

  public addEntrance() {
    this.modalService.openModal(openDialogByParams.createEntrance);
  }

  public edit(entrance: EventV2Document): void {
    if (!!entrance?.selectedFlow?.formValue && 'camera' in entrance.selectedFlow.formValue) {
      const camera = entrance.selectedFlow.formValue.camera[0];

      const cameraItem: EdgeCamera.CameraItem = {
        edgeOnly: {
          cameraId: camera?.cameraId,
        },
        lineCrossing: {
          ...camera.lineCrossing,
          name: entrance.name,
        },
      };

      const data: UiTrafficControlDialogData = {
        camera: cameraItem,
        trafficControl: _.cloneDeep(camera.lineCrossing) as AlertEventTrafficControl,
        occupancy: true,
        asLineCrossing: true,
      };

      this.dialog
        .open(UiTrafficControlDialogComponent, {
          data,
          panelClass: 'modal-no-padding',
          maxWidth: '115vh',
        })
        .afterClosed()
        .subscribe((result: UiTrafficControlDialogResult) => {
          if (!result) {
            return;
          }

          const updatedCamera = _.cloneDeep(camera);
          const updatedEntrance = _.cloneDeep(entrance);

          updatedCamera.trafficControl = _.cloneDeep(result.trafficControl) as AlertEventTrafficControl;
          updatedCamera.lineCrossing = _.cloneDeep(result.trafficControl) as unknown as AlertEventLineCrossing;

          updatedEntrance.name = (<AlertEventTrafficControl>result.trafficControl).name;
          const formValue: any = updatedEntrance.selectedFlow.formValue;
          formValue.camera[0] = updatedCamera;
          entrance.name = updatedEntrance.name;

          updatedEntrance.settings.show = {
            disabled: true,
            on: [],
          };

          // Update the entrance in store and trigger save
          this.store$.dispatch(
            AlertsV2StoreActions.setSelectedFlow({ selectedFlow: updatedEntrance.selectedFlow }),
          );
          this.store$.dispatch(AlertsV2StoreActions.setSelectedEvent({ selectedEvent: updatedEntrance }));
          this.store$.dispatch(AlertsV2StoreActions.setSettings({ settings: updatedEntrance.settings }));
          this.store$.dispatch(AlertsV2StoreActions.saveAlert());
        });
    }
  }

  private getCameraNameFormatted(entrance: EventV2Document): string {
    const cameraData = this.camera(entrance);
    if (!cameraData?.cameraId) {
      return '';
    }

    const namesMap = this.cameraNames();
    return namesMap?.[cameraData.cameraId] ?? '';
  }

  public camera(entrance: EventV2Document): CameraLookup {
    if (entrance?.selectedFlow?.formValue && 'camera' in entrance?.selectedFlow?.formValue) {
      if (!entrance?.selectedFlow?.formValue?.camera) {
        return null;
      }
      return entrance?.selectedFlow?.formValue?.camera[0];
    }
    return null;
  }

  public onRowAction(event: {
    actionId: string;
    row: EventV2Document;
  }): void {
    if (event.actionId === 'edit') {
      this.edit(event.row);
    } else if (event.actionId === 'delete') {
      this.delete(event.row);
    }
  }

  private openAddEntranceDialog() {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: {
        multi: false,
        zoneSelection: true,
        zoneSelectionType: ZoneSelectionType.COUNTING,
        cameraLookupEnabled: true,
        selectedValueInputType: CameraPickerModels.SelectedCameraInputType.CameraLookup,
        validateZonesEnabled: true,
      },
    };

    const dialogRef = this.dialog.open(CameraPickerModalComponent, {
      width: '600px',
      panelClass: 'modal-no-padding',
      height: '70vh',
      disableClose: true,
      data,
    });

    dialogRef.afterClosed().subscribe((selectedCameras: Dictionary<CameraPickerModels.SelectedCamera>) => {
      if (!selectedCameras) {
        return;
      }
      const cameras = Object.values(selectedCameras);
      if (cameras?.length) {
        const camera = cameras[0];
        this.store$.dispatch(
          AlertsV2StoreActions.createCountingAlert({
            camera: {
              cameraId: camera.cameraId,
              edgeId: camera.edgeId,
              locationId: camera.locationId,
              ...camera.cameraLookup,
            },
          }),
        );
      }
    });

    return dialogRef;
  }

  protected readonly loaderColor = PreloaderColor;
}
